import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gravarGestor, sessao } from '@/auth/session-store';
import { buscarGestoresDoUsuario, vincularSessaoAoGestor } from '../api/gestor.api';
import type { Gestor } from '@/services/Auth/types';

export interface UsoTrocaGestor {
  gestores: Gestor[];
  gestorAtualCnpj: string | null;
  carregando: boolean;
  erro: boolean;
  trocando: string | null;
  erroTroca: string | null;
  trocar: (gestor: Gestor) => void;
}

/**
 * Busca a lista de gestores vinculados ao e-mail logado sob demanda — só
 * quando `aberto` (controlado por quem usa o hook: o modal do header ou o
 * gate `RequireGestor`) — e troca de gestor chamando `Auth/SelecionarGestor`:
 * o backend reemite o JWT já com `cnpj_gestor`, sem header manual.
 *
 * `obrigatorio`: se só existe 1 gestor vinculado, seleciona automaticamente
 * sem esperar clique (usado pelo `RequireGestor`).
 */
export function useTrocaGestor(aberto: boolean, obrigatorio = false): UsoTrocaGestor {
  const { email, cnpjGestor } = sessao();
  const [trocando, setTrocando] = useState<string | null>(null);
  const [erroTroca, setErroTroca] = useState<string | null>(null);
  const [jaTentouAutoSelecao, setJaTentouAutoSelecao] = useState(false);

  const consulta = useQuery({
    queryKey: ['gestores-do-usuario', email],
    queryFn: () => buscarGestoresDoUsuario(email ?? ''),
    enabled: aberto && Boolean(email),
    staleTime: 5 * 60 * 1000,
  });

  const gestores = useMemo(() => consulta.data?.data ?? [], [consulta.data]);

  const trocar = useCallback((gestor: Gestor) => {
    if (!gestor.CNPJ_Gestor) return;
    setTrocando(gestor.CNPJ_Gestor);
    setErroTroca(null);

    void (async () => {
      try {
        await vincularSessaoAoGestor(gestor.CNPJ_Gestor);
        gravarGestor(gestor.CNPJ_Gestor, gestor.Nome);
        // Reload completo: leituras de sessão fora do ciclo React (getCnpjGestor
        // etc.) não reagiriam a um invalidateQueries.
        window.location.assign('/');
      } catch (falha) {
        const mensagem =
          falha instanceof Error && falha.message
            ? falha.message
            : 'Não foi possível selecionar o gestor.';
        setErroTroca(mensagem);
        setTrocando(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      !obrigatorio ||
      jaTentouAutoSelecao ||
      consulta.isPending ||
      consulta.isError ||
      trocando ||
      gestores.length !== 1
    ) {
      return;
    }
    setJaTentouAutoSelecao(true);
    trocar(gestores[0]!);
  }, [
    obrigatorio,
    jaTentouAutoSelecao,
    consulta.isPending,
    consulta.isError,
    gestores,
    trocando,
    trocar,
  ]);

  return {
    gestores,
    gestorAtualCnpj: cnpjGestor,
    carregando: consulta.isPending && aberto,
    erro: consulta.isError,
    trocando,
    erroTroca,
    trocar,
  };
}
